import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { InvoiceKind } from '@qlkho-v3/shared';

import { api, ApiError } from './api';
import { CloseIcon, EyeIcon, EyeOffIcon, LockIcon, UnlockIcon } from './icons';

export interface LockableInboundInvoice {
  id: string;
  sku: string;
  isLocked: boolean;
}

export function InvoiceLockModal({
  kind,
  invoice,
  username,
  onClose,
  onChanged
}: {
  kind: InvoiceKind;
  invoice: LockableInboundInvoice;
  username: string;
  onClose: () => void;
  onChanged: (message: string) => void;
}) {
  const outbound = kind === 'outbound';
  const passwordInput = useRef<HTMLInputElement>(null);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const willLock = !invoice.isLocked;

  useEffect(() => passwordInput.current?.focus(), []);

  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !saving) onClose();
    };
    document.addEventListener('keydown', close);
    return () => document.removeEventListener('keydown', close);
  }, [onClose, saving]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!password) {
      setError('Vui lòng nhập mật khẩu.');
      passwordInput.current?.focus();
      return;
    }

    setSaving(true);
    setError('');
    try {
      const result = await api<{ invoice: { message: string } }>(
        `/api/invoices/${kind}/${invoice.id}/lock`,
        {
          method: 'PUT',
          body: JSON.stringify({ isLocked: willLock, password })
        }
      );
      onChanged(result.invoice.message);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : `Không thể ${willLock ? 'khóa' : 'mở khóa'} hóa đơn.`);
      if (reason instanceof ApiError && reason.code === 'INVALID_PASSWORD') {
        setPassword('');
        setShowPassword(false);
      }
      passwordInput.current?.focus();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="drawer-backdrop lock-backdrop" role="presentation">
      <section className={`lock-modal ${willLock ? 'is-locking' : 'is-unlocking'}`} role="dialog" aria-modal="true" aria-labelledby="lock-title">
        <header className="drawer-header">
          <h2 id="lock-title">{willLock ? 'Khóa' : 'Mở khóa'} hóa đơn {outbound ? 'xuất' : 'nhập'}</h2>
          <button type="button" className="icon-button" disabled={saving} onClick={onClose} aria-label="Đóng"><CloseIcon /></button>
        </header>
        <form onSubmit={submit}>
          <div className="lock-content">
            <div className="lock-context">
              <div><span>Mã hóa đơn</span><strong>{invoice.sku}</strong></div>
              <div><span>Thao tác</span><b>{willLock ? 'Khóa cập nhật' : 'Cho phép cập nhật'}</b></div>
            </div>
            <p className="lock-description">{willLock ? 'Sau khi khóa, hóa đơn không thể chỉnh sửa cho đến khi được mở khóa.' : 'Sau khi mở khóa, hóa đơn có thể được chỉnh sửa lại.'}</p>
            <div className="field lock-password">
              <label htmlFor="lock-password">Mật khẩu đăng nhập</label>
              <input className="credential-username" type="text" name="username" autoComplete="username" value={username} readOnly tabIndex={-1} aria-hidden="true" />
              <div className="password-control">
                <input ref={passwordInput} id="lock-password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" value={password} aria-invalid={Boolean(error)} onChange={(event) => { setPassword(event.target.value); setError(''); }} />
                <button type="button" className="password-toggle" aria-label={showPassword ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'} aria-pressed={showPassword} title={showPassword ? 'Ẩn mật khẩu' : 'Hiện mật khẩu'} onClick={() => { setShowPassword((current) => !current); passwordInput.current?.focus(); }}>
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
              {error ? <span className="field-error" role="alert">{error}</span> : null}
            </div>
          </div>
          <footer className="drawer-footer">
            <button type="button" className="secondary-action" disabled={saving} onClick={onClose}>Hủy</button>
            <button className="primary-action lock-confirm" disabled={saving}>{saving ? <span className="button-spinner" /> : willLock ? <LockIcon /> : <UnlockIcon />} {saving ? 'Đang lưu…' : `Xác nhận ${willLock ? 'khóa' : 'mở khóa'}`}</button>
          </footer>
        </form>
      </section>
    </div>
  );
}
